import React, { useState, useContext, useEffect, useCallback } from 'react';
import styles from './styles/SecuritySettings.module.css';
import AuthContext from '../../context/AuthContext';
import {
    checkUsernameAvailability as checkUsernameAvailabilityAPI,
    changeUsername,
} from '../../services/UserServices';
import { changePassword } from '../../services/AuthServices';
import debounce from 'lodash.debounce';
import Spinner from '../loading/Spinner';

const initialPasswordFormState = {
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: ''
};

const initialUsernameFormState = {
    currentPassword: '',
    newUsername: ''
};

const SecuritySettings = () => {
    const { user, setUser } = useContext(AuthContext);
    
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordForm, setPasswordForm] = useState(initialPasswordFormState);
    const [passwordError, setPasswordError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(null);
    
    const [showUsernameForm, setShowUsernameForm] = useState(false);
    const [usernameForm, setUsernameForm] = useState(initialUsernameFormState);
    const [usernameAvailable, setUsernameAvailable] = useState(null);
    const [checkingUsername, setCheckingUsername] = useState(false);
    const [usernameError, setUsernameError] = useState(null);
    const [usernameSuccess, setUsernameSuccess] = useState(null);
    
    const [loading, setLoading] = useState(false);

    const checkUsernameAvailability = useCallback(debounce(async (usernameToCheck) => {
        if (!usernameToCheck.trim() || (user && usernameToCheck === user.username)) {
            setUsernameAvailable(null);
            setUsernameError(null);
            setCheckingUsername(false);
            return;
        }

        let showSpinnerTimeoutId = null;
        try {
            setUsernameError(null);
            setUsernameAvailable(null); 

            showSpinnerTimeoutId = setTimeout(() => {
                setCheckingUsername(true);
            }, 300);

            const data = await checkUsernameAvailabilityAPI(usernameToCheck);

            clearTimeout(showSpinnerTimeoutId);

            setUsernameAvailable(data.available);
            if (!data.available) {
                setUsernameError('Username is already taken');
            }
        } catch (err) {
            clearTimeout(showSpinnerTimeoutId);
            setUsernameError(err.response?.data?.errorMessage || err.message || 'Error checking username availability');
            setUsernameAvailable(false);
        } finally {
            clearTimeout(showSpinnerTimeoutId); 
            setCheckingUsername(false);
        }
    }, 700), [user]);

    useEffect(() => {
        return () => {
            checkUsernameAvailability.cancel();
        };
    }, [checkUsernameAvailability]);

    const handlePasswordChange = useCallback((e) => {
        setPasswordForm(prevForm => ({
            ...prevForm,
            [e.target.name]: e.target.value
        }));
    }, []);

    const handlePasswordSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setPasswordError(null);
        setPasswordSuccess(null);

        try {
            const data = await changePassword(passwordForm);

            setPasswordSuccess(data.successMessage);
            setPasswordForm(initialPasswordFormState);
            setShowPasswordForm(false);
        } catch (err) {
            setPasswordError(err.response?.data?.errorMessage || err.message || 'Password change failed');
        } finally {
            setLoading(false);
        }
    }, [passwordForm]);

    const handleUsernameFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setUsernameForm(prev => ({ ...prev, [name]: value }));
        if (name === 'newUsername') {
            setUsernameError(null); 
            setUsernameAvailable(null);
            if (value.trim()) {
                checkUsernameAvailability(value);
            } else {
                checkUsernameAvailability.cancel();
                setCheckingUsername(false);
                setUsernameAvailable(null);
            }
        }
    }, [checkUsernameAvailability]);


    const handleUsernameSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setUsernameError(null);
        setUsernameSuccess(null);

        try {
            const data = await changeUsername(usernameForm);

            setUsernameSuccess(data.successMessage);
            if (setUser && data.newUsername) {
                setUser(prevUser => ({ ...prevUser, username: data.newUsername }));
            }
            setUsernameForm(initialUsernameFormState);
            setShowUsernameForm(false);
            setUsernameAvailable(null);
        } catch (err) {
            setUsernameError(err.response?.data?.errorMessage || err.message || 'Username change failed');
        } finally {
            setLoading(false);
        }
    }, [usernameForm, setUser]);

    const handleCancelPasswordForm = useCallback(() => {
        setPasswordForm(initialPasswordFormState);
        setShowPasswordForm(false);
        setPasswordError(null);
        setPasswordSuccess(null);
    }, []);

    const handleCancelUsernameForm = useCallback(() => {
        setUsernameForm(initialUsernameFormState);
        setShowUsernameForm(false);
        setUsernameError(null);
        setUsernameSuccess(null);
        setUsernameAvailable(null);
        setCheckingUsername(false);
        checkUsernameAvailability.cancel();
    }, [checkUsernameAvailability]);


    return (
        <div className={styles.securityContainer}>
            <h2>Security Settings</h2>

            <div className={styles.section}>
                <h3>Change Password</h3>
                {!showPasswordForm ? (
                    <button 
                        className={styles.changeButton}
                        onClick={() => setShowPasswordForm(true)}
                        disabled={loading}
                    >
                        Change Password
                    </button>
                ) : (
                    <form onSubmit={handlePasswordSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label>Current Password</label>
                            <input
                                type="password"
                                name="currentPassword"
                                value={passwordForm.currentPassword}
                                onChange={handlePasswordChange}
                                required
                                autoComplete="current-password"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>New Password</label>
                            <input
                                type="password"
                                name="newPassword"
                                value={passwordForm.newPassword}
                                onChange={handlePasswordChange}
                                required
                                autoComplete="new-password"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Confirm New Password</label>
                            <input
                                type="password"
                                name="newPasswordConfirm"
                                value={passwordForm.newPasswordConfirm}
                                onChange={handlePasswordChange}
                                required
                                autoComplete="new-password"
                            />
                        </div>

                        {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
                        {passwordSuccess && <div className={styles.successMessage}>{passwordSuccess}</div>}

                        <div className={styles.buttonGroup}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancelPasswordForm}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.submitButton}
                                disabled={loading}
                            >
                                {loading ? 'Updating...' : 'Change Password'}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <div className={styles.section}>
                <h3>Change Username</h3>
                {!showUsernameForm ? (
                    <button 
                        className={styles.changeButton}
                        onClick={() => setShowUsernameForm(true)}
                        disabled={loading}
                    >
                        Change Username
                    </button>
                ) : (
                    <form onSubmit={handleUsernameSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label>Current Password</label>
                            <input
                                type="password"
                                name="currentPassword"
                                value={usernameForm.currentPassword}
                                onChange={handleUsernameFormChange}
                                required
                                autoComplete="current-password"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>New Username</label>
                            <div className={styles.usernameInputContainer}>
                                <input
                                    type="text"
                                    name="newUsername"
                                    value={usernameForm.newUsername}
                                    onChange={handleUsernameFormChange}
                                    required
                                    autoComplete="username"
                                />
                                {checkingUsername && (
                                    <span className={styles.checkingIndicator}><Spinner/></span>
                                )}
                                {usernameAvailable === true && !checkingUsername && (
                                    <span className={styles.available}>
                                        Available
                                    </span>
                                )}
                                {usernameAvailable === false && !checkingUsername && (
                                    <span className={styles.taken}>
                                        Taken
                                    </span>
                                )}
                            </div>
                        </div>

                        {usernameError && <div className={styles.errorMessage}>{usernameError}</div>}
                        {usernameSuccess && <div className={styles.successMessage}>{usernameSuccess}</div>}

                        <div className={styles.buttonGroup}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancelUsernameForm}
                                disabled={loading}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className={styles.submitButton}
                                disabled={loading || usernameAvailable !== true || checkingUsername}
                            >
                                {loading ? 'Updating...' : 'Change Username'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SecuritySettings;